import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import styles from './floating-buttons.module.scss';
import debounce from 'just-debounce-it';
import CommentsContext from '../../../contexts/comments';
import { CtaCommentsIcon } from '@buzzfeed/react-components';
import { isNativeShareEligible } from '../../../utils/sharing';
import NativeShareButton from '../../NativeShareButton';
import { trackClientContentAction } from '../../../hooks/analytics/client-event-tracking';
import { useTranslation } from '../../../i18n';


export const FloatingButtons = ({ buzz, className = '', isFloatingButtonsEnd, commentCount }) => {
  const [isMobileShareEnabled, setIsMobileShareEnabled] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [hasScrolledDown, setHasScrolledDown] = useState(false);
  const [isEndTriggered, setIsEndTriggered] = useState(false);
  const [endPosition, setEndPosition] = useState(null);
  const isMountedRef = useRef(true);
  const { t } = useTranslation('common');

  const {
    commentsEnabled,
    isCommentsPanelOpen,
    toggleCommentsPanel
  } = useContext(CommentsContext);
  const showCommentButton = commentsEnabled && !isCommentsPanelOpen && !buzz.isShopping;

  const onCommentsClick = useCallback(() => {
    trackClientContentAction(buzz, {
      item_name: 'comment-cta',
      item_type: 'button',
      action_value: 'comment-cta',
      action_type: 'navigate',
      unit_name: buzz.id,
      unit_type: 'buzz_head',
      subunit_type: 'component',
      subunit_name: 'floating_comments',
    });
    toggleCommentsPanel(true);
  }, [buzz, toggleCommentsPanel]);

  // set if user can natively share / show buttons on mobile device
  useEffect(() => {
    if (isNativeShareEligible() && !buzz?.shouldHideBuzzSharing && !buzz?.isQuiz) {
      setIsMobileShareEnabled(true);
    }
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, [buzz]);

  // set if float buttons are currently displayed
  useEffect(() => {
    setIsVisible(hasScrolledDown && !isEndTriggered);
  }, [hasScrolledDown, isEndTriggered]);

  // Handle scroll updates with debounce
  useEffect(() => {
    const checkScrollStatus = () => {
      const scrollY = window.pageYOffset || window.scrollY || 0;
      setHasScrolledDown(scrollY > 20);
      if (endPosition != null) {
        const contentOffset = 50;
        setIsEndTriggered((scrollY - endPosition) > contentOffset);
      }
    };

    const debouncedCheckScrollStatus = debounce(checkScrollStatus, 100);
    window.addEventListener('scroll', debouncedCheckScrollStatus);
    checkScrollStatus(); // Initial check

    return () => {
      window.removeEventListener('scroll', debouncedCheckScrollStatus);
    };
  }, [endPosition]);

  useEffect(() => {
    if (isFloatingButtonsEnd && isMountedRef.current) {
      const scrollY = window.pageYOffset || window.scrollY || 0;
      setEndPosition(scrollY);
    }
  }, [isFloatingButtonsEnd]);


  return (
    <div className={`${styles.floatingButtonsWrapper} ${className}
      ${isVisible ? styles.visible : ''}
      ${!hasScrolledDown ? styles.inactive : ''}`}>
      {showCommentButton && <button className={styles.commentButton} onClick={onCommentsClick} aria-label={t('comments')}>
        <CtaCommentsIcon className={styles.icon} color="#5246f5" />
        {commentCount > 4 ?
          <>
            <span>{commentCount}</span>
            <span className={styles.comments}>{t('comments')}</span>
          </>
          : <span className={styles.comments}>{t('comment')}</span>
        }
      </button>}
      {isMobileShareEnabled && <div className={styles.nativeShare}>
        <NativeShareButton
          type='page_level'
          variant='icon_only'
          includeShortTitle
          trackingData={{
            unit_type: 'modal',
            unit_name: buzz?.id,
            subunit_type: 'component',
            subunit_name: 'floating_share',
          }}
        />
      </div>}
    </div>
  );
};

FloatingButtons.propTypes = {
  buzz: PropTypes.object.isRequired,
  commentCount: PropTypes.number,
  className: PropTypes.string,
  isFloatingButtonsEnd: PropTypes.bool,
};

export default FloatingButtons;
